import type { ProviderClaim } from '../../../api/server/models/ProviderClaim';
import { ProviderClaimResponsibilityCategoryType } from '../../../api/server/models/ProviderClaimResponsibilityCategoryType';
import type { ProviderPatientClaimStatement } from '../../../api/server/models/ProviderPatientClaimStatement';
import { parseMoney } from './statement-format.utils';

export type StatementClaimFinancials = {
    charge: number;
    insurance: number;
    adjustment: number;
    patientResp: number;
};

const sumServiceLineCharges = (claim: ProviderPatientClaimStatement): number =>
    (claim.serviceLines || []).reduce((sum, line) => sum + parseMoney(line.chargeAmount?.amount), 0);

/** True when ProviderClaim shows adjudication even if statement still says pending. */
export const hasProviderClaimAdjudicationEvidence = (providerClaim?: ProviderClaim): boolean => {
    if (!providerClaim) return false;

    if (providerClaim.claimResponsibilityCategoryType === ProviderClaimResponsibilityCategoryType.CLAIMPAID) {
        return true;
    }
    if ((providerClaim.payments ?? 0) > 0) return true;
    if (parseMoney(providerClaim.payerAmount?.amount) > 0) return true;
    if (parseMoney(providerClaim.allowedAmount?.amount) > 0) return true;

    return false;
};

export const isStatementClaimPendingAdjudication = (
    claim: Pick<ProviderPatientClaimStatement, 'pendingAdjudication' | 'payerPayments'>,
    providerClaim?: ProviderClaim,
): boolean => {
    if ((claim.payerPayments || []).length > 0) return false;
    if (hasProviderClaimAdjudicationEvidence(providerClaim)) return false;
    return !!claim.pendingAdjudication;
};

/**
 * Prefer statement payerPayments; when missing/null, fall back to ProviderClaim aggregates
 * (payerAmount / patientAmount) so Claims tab & balance hero stay accurate.
 */
export const resolveStatementClaimFinancials = (
    claim: ProviderPatientClaimStatement,
    providerClaim?: ProviderClaim,
): StatementClaimFinancials => {
    const charge = sumServiceLineCharges(claim);
    const payerPayments = claim.payerPayments || [];

    if (payerPayments.length > 0) {
        const insurance = payerPayments.reduce(
            (sum, payment) => sum + parseMoney(payment.payerAmount?.amount),
            0,
        );
        const adjustment = payerPayments.reduce(
            (sum, payment) => sum + parseMoney(payment.adjustmentAmount?.amount),
            0,
        );
        return {
            charge,
            insurance,
            adjustment,
            patientResp: Math.max(0, charge - insurance - adjustment),
        };
    }

    if (providerClaim) {
        const insurance = parseMoney(providerClaim.payerAmount?.amount);
        const patientResp = Math.max(0, parseMoney(providerClaim.patientAmount?.amount));
        const adjustment = Math.max(0, charge - insurance - patientResp);
        return { charge, insurance, adjustment, patientResp };
    }

    return {
        charge,
        insurance: 0,
        adjustment: 0,
        patientResp: Math.max(0, charge),
    };
};
